import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../api';
import Drawer from '../UI/Drawer';
import { primaryBtn, primaryBtnStyle, ghostBtn, ghostBtnStyle, inputCls, inputStyle, fieldLabel, fieldLabelStyle, errMessage } from '../UI/ink';

const PRIORITIES = [['critical', 'Critical'], ['high', 'High'], ['medium', 'Medium'], ['low', 'Low']];

// Opens a case with this finding linked, then goes to it.
const NewCaseDrawer = ({ finding, onClose }) => {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState('medium');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!finding) return;
    const host = finding.server_hostname || finding.server_id;
    setTitle(host ? `${finding.title} on ${host}` : finding.title);
    setPriority(PRIORITIES.some(([v]) => v === finding.severity) ? finding.severity : 'low');
    setDescription('');
    setError(null);
  }, [finding]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!title.trim()) { setError('Title is required.'); return; }
    setBusy(true);
    setError(null);
    try {
      const res = await apiClient.post('/api/cases', { title: title.trim(), description: description.trim() || null, priority });
      const caseId = res.data?.id;
      if (caseId) await apiClient.post(`/api/cases/${caseId}/findings`, { finding_id: finding.id });
      onClose();
      if (caseId) navigate(`/cases/${caseId}`);
    } catch (err) {
      setError(errMessage(err, 'Failed to create case.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      open={!!finding}
      onClose={onClose}
      kicker="CASES"
      title="New case from this finding"
      subtitle="The finding is linked to the new case so the investigation is tracked in one place."
      footer={(
        <>
          <span />
          <div className="flex gap-2.5">
            <button type="button" onClick={onClose} className={ghostBtn} style={ghostBtnStyle}>Cancel</button>
            <button type="submit" form="finding-case-form" disabled={busy} className={primaryBtn} style={primaryBtnStyle}>{busy ? 'Creating…' : 'Create case'}</button>
          </div>
        </>
      )}
    >
      <form id="finding-case-form" onSubmit={submit} className="flex flex-col gap-5">
        {error && <div role="alert" className="px-3.5 py-2.5 rounded-[10px] border text-[13px]" style={{ borderColor: 'var(--sev-crit-fg)', color: 'var(--sev-crit-fg)', background: 'var(--sev-crit-bg)' }}>{error}</div>}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="fcase-title" className={fieldLabel} style={fieldLabelStyle}>Title</label>
          <input id="fcase-title" type="text" required value={title} onChange={e => setTitle(e.target.value)} className={inputCls} style={inputStyle} />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="fcase-priority" className={fieldLabel} style={fieldLabelStyle}>Priority</label>
          <select id="fcase-priority" value={priority} onChange={e => setPriority(e.target.value)} className={inputCls} style={inputStyle}>
            {PRIORITIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="fcase-desc" className={fieldLabel} style={fieldLabelStyle}>Notes <span className="font-normal" style={{ color: 'var(--ink-faint)' }}>(optional)</span></label>
          <textarea id="fcase-desc" rows={3} value={description} onChange={e => setDescription(e.target.value)} className="box-border px-3 py-2.5 rounded-[10px] border text-sm resize-none outline-none" style={inputStyle} />
        </div>
      </form>
    </Drawer>
  );
};

export default NewCaseDrawer;
